package com.klantenstop.web.pages.admin.curatedcities.manage

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.klantenstop.web.components.layout.AdminPageLayout
import com.klantenstop.web.data.models.domain.Province
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.utils.withAlpha
import com.materialkobweb.components.toast.ToastManager
import com.materialkobweb.components.widgets.DsCheckboxInput
import com.materialkobweb.components.widgets.DsEditableField
import com.materialkobweb.components.widgets.DsReadOnlyField
import com.materialkobweb.components.widgets.DsSelect
import com.materialkobweb.components.widgets.DsSpinner
import com.materialkobweb.components.widgets.FilledButton
import com.materialkobweb.components.widgets.OutlinedButton
import com.materialkobweb.components.widgets.SpinnerSize
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.utils.rememberViewModel
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flex
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Page("/admin/steden/beheren")
@Composable
fun ManageCuratedCityPage() {
    val ctx = rememberPageContext()
    val curatedCityId = ctx.route.params["id"]
    val viewModel = rememberViewModel(cached = false) { ManageCuratedCityViewModel(curatedCityId) }
    val state by viewModel.state.collectAsState()
    val isEdit = curatedCityId != null

    LaunchedEffect(Unit) {
        viewModel.effect.collectLatest { effect ->
            when (effect) {
                is ManageCuratedCityContract.Effect.NavigateBack ->
                    ctx.router.navigateTo(Navigation.Screen.Admin.CuratedCities.Index.route)

                is ManageCuratedCityContract.Effect.ShowError -> ToastManager.error(effect.message)
                is ManageCuratedCityContract.Effect.ShowSuccess -> ToastManager.success(effect.message)
            }
        }
    }

    AdminPageLayout(
        title = if (isEdit) "Stad bewerken" else "Stad aanmaken"
    ) {
        ManageCuratedCityContent(
            state = state,
            isEdit = isEdit,
            sendIntent = viewModel::sendIntent
        )
    }
}

@Composable
fun ManageCuratedCityContent(
    state: ManageCuratedCityContract.State,
    isEdit: Boolean,
    sendIntent: (ManageCuratedCityContract.Intent) -> Unit,
) {
    val breakpoint = rememberBreakpoint()

    if (state.isLoading) {
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            DsSpinner(size = SpinnerSize.Normal)
        }
        return
    }

    Column(
        modifier = Modifier
            .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
            .fillMaxHeight()
            .maxWidth(1000.px)
            .padding(leftRight = 24.px, topBottom = 58.px),
        verticalArrangement = Arrangement.spacedBy(24.px)
    ) {
        // Slug is auto-generated server-side and locked after creation.
        if (isEdit) {
            DsReadOnlyField(
                modifier = Modifier.fillMaxWidth(),
                label = "Slug",
                value = state.curatedCity?.slug.orEmpty()
            )
        }

        // Name field
        DsEditableField(
            modifier = Modifier.fillMaxWidth(),
            id = "curated-city-name",
            label = "Naam *",
            placeholder = "Naam van de stad",
            value = state.name,
            valid = true,
            onValueChange = { sendIntent(ManageCuratedCityContract.Intent.UpdateName(it)) },
            readOnly = state.isGeocoding,
            required = true,
            backgroundColor = MaterialColorVars.SurfaceContainer.value(),
            focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
        )

        // Province dropdown
        DsSelect(
            modifier = Modifier.fillMaxWidth(),
            id = "curated-city-province",
            label = "Provincie *",
            items = Province.entries.map { it.displayName },
            preselectedItem = state.province?.displayName,
            placeholder = "Kies een provincie",
            enabled = !state.isGeocoding,
            required = true,
            onItemSelect = { index, _ ->
                Province.entries.getOrNull(index)?.let { sendIntent(ManageCuratedCityContract.Intent.UpdateProvince(it)) }
            }
        )

        // Geocode-assist: pre-fills latitude/longitude from the name + province, but both stay editable.
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            OutlinedButton(
                modifier = Modifier.flex(1),
                enabled = !state.isGeocoding && state.name.isNotBlank(),
                onClick = { sendIntent(ManageCuratedCityContract.Intent.GeocodeCity) }
            ) {
                if (state.isGeocoding) DsSpinner(size = SpinnerSize.Small) else Text("Locatie opzoeken")
            }
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.px)
        ) {
            // Latitude field
            DsEditableField(
                modifier = Modifier.flex(1),
                id = "curated-city-latitude",
                label = "Breedtegraad *",
                placeholder = "bv. 50.8503",
                value = state.latitude,
                valid = true,
                onValueChange = { sendIntent(ManageCuratedCityContract.Intent.UpdateLatitude(it)) },
                readOnly = state.isGeocoding,
                required = true,
                backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
            )

            // Longitude field
            DsEditableField(
                modifier = Modifier.flex(1),
                id = "curated-city-longitude",
                label = "Lengtegraad *",
                placeholder = "bv. 4.3517",
                value = state.longitude,
                valid = true,
                onValueChange = { sendIntent(ManageCuratedCityContract.Intent.UpdateLongitude(it)) },
                readOnly = state.isGeocoding,
                required = true,
                backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
            )
        }

        DsCheckboxInput(
            id = "curated-city-is-featured",
            label = "Uitgelicht (max. 4 per provincie, getoond op algemene pagina)",
            checked = state.isFeatured,
            onCheckedChange = { sendIntent(ManageCuratedCityContract.Intent.UpdateIsFeatured(it)) }
        )

        // Action buttons
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(12.px)
        ) {
            OutlinedButton(
                modifier = Modifier.flex(1),
                enabled = !state.isSaving,
                onClick = { sendIntent(ManageCuratedCityContract.Intent.NavigateBack) }
            ) {
                Text("Annuleren")
            }
            FilledButton(
                modifier = Modifier.flex(1),
                enabled = state.hasChanged && !state.isSaving,
                onClick = { sendIntent(ManageCuratedCityContract.Intent.SaveCuratedCity) }
            ) {
                if (state.isSaving) DsSpinner(size = SpinnerSize.Small) else Text("Opslaan")
            }
        }
    }
}
